import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TagKind, TagScope } from '@reditelji/shared';
import { inOrder, latestVersion, mergeSaved } from './cache';
import { api } from './client';
import { notifyApiError } from './posts';

export interface TagRow {
  id: string;
  label: string;
  colour: string;
  scope: TagScope;
  kind: TagKind;
  ownerCoordinatorId: string | null;
  updatedAt: string;
}

/** The tags of one vocabulary this coordinator can see: shared ones and their own private ones. */
export function useTags(kind: TagKind) {
  return useQuery({
    queryKey: ['tags', kind],
    queryFn: () => api<TagRow[]>(`/tags?kind=${kind}`),
  });
}

export function useTagMutations(kind: TagKind) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['tags', kind] });
    // A deleted tag takes its links with it, and the lists carry the links.
    void qc.invalidateQueries({ queryKey: [kind === 'post' ? 'posts' : 'volunteers'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };
  const common = { onError: notifyApiError, onSettled: invalidate };
  const listKey = ['tags', kind];
  return {
    create: useMutation({
      mutationFn: (input: { label: string; colour: string; scope: TagScope }) =>
        api<TagRow>('/tags', { method: 'POST', body: JSON.stringify({ ...input, kind }) }),
      ...common,
    }),
    // The tag manager saves a name on blur, and a swatch or the scope button
    // is often the very click that blurs it: two writes to one tag, in order.
    update: useMutation({
      mutationFn: ({
        id,
        ...patch
      }: { id: string } & Partial<{ label: string; colour: string; scope: TagScope }>) =>
        api<TagRow>(`/tags/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ ...patch, updatedAt: latestVersion(qc, listKey, id) }),
        }),
      ...inOrder(qc, `tags:${kind}`, listKey, invalidate),
      onSuccess: (saved, { id, ...patch }) =>
        mergeSaved<TagRow>(qc, listKey, id, saved, [
          ...Object.keys(patch),
          ...('scope' in patch ? ['ownerCoordinatorId'] : []),
        ]),
      onError: notifyApiError,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/tags/${id}`, { method: 'DELETE' }),
      ...common,
    }),
  };
}
